// Keep full chapter/topic data in catalog.json build-only. The global SEO helper
// imports this compact index so every marketing route does not download outlines.
import index from './index.json';
import advancedFunctions from './advanced-functions.json';

export const RESOURCE_SITE_URL = 'https://www.prepbox.io';
export const resourceCatalog = index;
export const EXCLUDED_WORKBOOK_SLUGS = ['prepbox-math-1', 'pre-algebra-quick-review', 'algebra-1-quick-review'];


// A small opt-in collection retains useful public links while the live API
// outline loads or is unavailable. It is not a full course inventory.
export function getAdvancedFunctionsCollection(courseId) {
    const course = advancedFunctions.courses.find((entry) => entry.id === Number(courseId));
    if (!course) return null;
    const lessons = advancedFunctions.lessons.filter((entry) => course.topicIds.includes(entry.id));
    return {
        course, lessons,
        groups: advancedFunctions.groups.map((group) => ({
            ...group, lessons: lessons.filter((entry) => entry.group === group.id),
        })).filter((group) => group.lessons.length),
        heading: 'Choose an interactive practice topic',
        intro: 'Explore selected interactive lessons for functions, polynomial algebra, and logarithms, with foundation review listed separately. This collection covers selected topics, not the whole course.',
        workbookPath: advancedFunctions.workbookPath,
        lessonUrl: (id) => `${advancedFunctions.siteUrl}/lessons/topic-${id}.html`,
    };
}

const home = { label: 'Home', path: '/' };
const lessonHub = { label: 'Free math lessons', path: '/lessons' };
const workbookHub = { label: 'Math workbooks', path: '/workbooks' };

export function getResourcePage(pathname) {
    if (typeof pathname !== 'string' || !pathname.startsWith('/') || /[?#]/.test(pathname)) return null;
    const path = (pathname.replace(/\/+$/, '') || '/').replace(/^\/worksheets(?=\/|$)/, '/workbooks');
    const canonical = `${RESOURCE_SITE_URL}${path}`;
    if (path === '/lessons') {
        return {
            kind: 'lesson-index', path, canonical, title: 'Free Math Lessons by Textbook and Grade | PrepBox',
            description: 'Browse free interactive math lessons and video explanations organized by textbook, chapter, and grade, from elementary math to Ontario high school courses.',
            h1: 'Free math lessons by textbook and grade',
            intro: 'Choose a textbook or course to explore its chapter outline. Available topics include interactive math lessons, video explanations, or both. PrepBox creates independent learning resources for elementary and high school math.',
            breadcrumbs: [home, lessonHub], resource: null,
        };
    }
    if (path === '/workbooks') {
        return {
            kind: 'workbook-index', path, canonical, title: 'Math Workbooks and Free PDFs | PrepBox',
            description: 'Browse math workbook chapter outlines and practice questions. Free PDFs are available for selected Canadian and Common Core workbooks.',
            h1: 'Math workbooks and free PDFs',
            intro: 'Explore math workbooks by curriculum and topic, with chapter outlines and practice questions. Selected books offer a free PDF through the download form. PDF availability is shown on each book page.',
            breadcrumbs: [home, workbookHub], resource: null,
        };
    }
    const lesson = index.lessons.find((resource) => resource.path === path);
    if (lesson) {
        const h1 = `${lesson.name} — Grade ${lesson.grade}`;
        const selected = getAdvancedFunctionsCollection(lesson.courseId);
        return {
            kind: 'lesson', path, canonical, title: `${lesson.name} — Grade ${lesson.grade} Math Lessons | PrepBox`,
            description: selected
                ? `Explore ${lesson.name} with interactive practice in functions, polynomial algebra, and logarithms, plus foundation review and a chapter outline.`
                : `Explore ${lesson.name} math topics for Grade ${lesson.grade}, organized by chapter and section, with available interactive lessons and video explanations.`,
            h1,
            intro: `Explore ${lesson.name} for Grade ${lesson.grade}. Follow the chapter and section outline to find available interactive lessons and video explanations. These are independent PrepBox learning resources; PrepBox is not affiliated with the textbook publisher.`,
            breadcrumbs: [home, lessonHub, { label: lesson.name, path }], resource: lesson,
        };
    }
    const workbook = index.workbooks.find((resource) => resource.path === path);
    if (workbook) {
        const pdfCopy = workbook.hasPdf
            ? 'A free PDF is available through the download form on this page.'
            : 'A downloadable PDF is not currently available for this book.';
        return {
            kind: 'workbook', path, canonical, title: `${workbook.name} — Math Practice${workbook.hasPdf ? ' and Free PDF' : ''} | PrepBox`,
            description: `Explore ${workbook.name}: chapter and section outlines and math practice questions.${workbook.hasPdf ? ' Request the free PDF on the book page.' : ''}`,
            h1: workbook.name,
            intro: `Explore ${workbook.name}, with its chapter outline and practice questions. ${pdfCopy}`,
            breadcrumbs: [home, workbookHub, { label: workbook.name, path }], resource: workbook,
        };
    }
    return null;
}
